import {
    applyMiddleware,
    compose,
    configureStore as configureReduxStore,
    EnhancedStore,
} from '@reduxjs/toolkit';
import { createEpicMiddleware } from 'redux-observable';

import {
    facebookMiddlewareReady,
    segmentMiddlewareReady,
} from '@perpay-web/fintech/actions/analytics';
import { getEpicManager } from '@perpay-web/fintech/epics/epics';
import { getDjangoTrackingMiddleware } from '@perpay-web/fintech/middleware/djangoTracking';
import { getGoogleAnalyticsMiddleware } from '@perpay-web/fintech/middleware/google';
import { sentryReduxEnhancer } from '@perpay-web/fintech/middleware/sentry';
import { getReducerManager } from '@perpay-web/fintech/reducers/reducers';
import rootReducer from '@perpay-web/fintech/reducers/root';
import { StaticReducers } from '@perpay-web/fintech/reducers/store.types';
import { configureAbTesting } from '@perpay-web/fintech/settings/configureAbTesting';
import {
    authentication,
    dynamicMiddleware,
} from '@perpay-web/fintech/settings/singletons';
import {
    installFacebookMiddleware,
    installSegmentMiddleware,
} from '@perpay-web/fintech/utils/analyticsUtils';
import {
    authenticatedJsonFetch,
    get,
    jsonFetch,
    patch,
    post,
    put,
} from '@perpay-web/fintech/utils/requestHandlers';
import { getStateFromNative } from '@perpay-web/native-client/stateFromNative';
import { Window } from '@perpay-web/types';

const djangoTrackingMiddleware = getDjangoTrackingMiddleware();
const googleAnalyticsMiddleware = getGoogleAnalyticsMiddleware();
const auth$ = authentication.token$;

const epicMiddleware = createEpicMiddleware({
    dependencies: {
        get,
        post,
        put,
        patch,
        authenticatedJsonFetch,
        jsonFetch,
        auth$,
        nativeState$: getStateFromNative(),
    },
});

const enhancers = compose(
    applyMiddleware(
        djangoTrackingMiddleware,
        googleAnalyticsMiddleware,
        epicMiddleware,
        dynamicMiddleware.middleware,
    ),
    sentryReduxEnhancer,
);

type RootState = ReturnType<typeof rootReducer> & {
    [K in keyof StaticReducers]: ReturnType<StaticReducers[K]>;
};

function configureStore(
    preloadedState: Partial<RootState> = {},
): EnhancedStore<RootState> {
    const reducerManager = getReducerManager();
    const epicManager = getEpicManager();

    const store = configureReduxStore({
        reducer: reducerManager.createRootReducer(rootReducer),
        devTools: !(window as Window).PRODUCTION,
        preloadedState,
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({ thunk: false }),
        enhancers: (getDefaultEnhancers) =>
            getDefaultEnhancers().concat(enhancers),
    }) as EnhancedStore<RootState>;

    epicMiddleware.run(epicManager.getRootEpic());

    // We dispatch ready actions here to let the analytics epic know to stop buffering events.
    // Since the analytics middleware load async, the events that they track need to be
    // buffered and re-emitted in order to be captured by the middleware after they load.
    // We dispatch in both the resolve and reject callback because we don't care if they were
    // successfully or unsuccessfully loaded, only that they have finished trying to load.
    const dispatchSegmentMiddlewareReady = () =>
        store.dispatch(segmentMiddlewareReady());
    installSegmentMiddleware().then(
        dispatchSegmentMiddlewareReady,
        dispatchSegmentMiddlewareReady,
    );
    const dispatchFacebookMiddlewareReady = () =>
        store.dispatch(facebookMiddlewareReady());
    installFacebookMiddleware().then(
        dispatchFacebookMiddlewareReady,
        dispatchFacebookMiddlewareReady,
    );

    configureAbTesting();

    return store;
}

const store = configureStore();

export default store;
